import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, ScanLine } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScanJoinPage() {
  const { linkId } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [passcode, setPasscode] = useState('');
  const [codeOk, setCodeOk] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/team/scan-join/${linkId}`)
      .then(r => setInfo(r.data))
      .catch(e => setError(e.response?.data?.detail || 'This scanner link is not valid'));
  }, [linkId]);

  const join = async () => {
    if (passcode.length !== 4) return toast.error('Enter the 4-digit passcode');
    setBusy(true);
    try {
      if (!codeOk) {
        await axios.post(`${API}/team/scan-join/${linkId}`, { passcode, verify_only: true });
        setCodeOk(true);
        return;
      }
      if (!name.trim()) { toast.error('Please enter your name'); return; }
      if (email && !email.includes('@')) { toast.error("That email doesn't look right"); return; }
      const r = await axios.post(`${API}/team/scan-join/${linkId}`, { passcode, name, email, phone });
      if (r.data.existing_account) {
        toast.info(r.data.message, { duration: 10000 });
        window.location.href = `/login?returnTo=${encodeURIComponent(r.data.scanner_url)}`;
        return;
      }
      localStorage.setItem('eventism_token', r.data.token);
      toast.success("You're in — opening the scanner…");
      window.location.href = r.data.scanner_url;
    } catch (e) {
      const msg = e.response?.data?.detail || 'Could not join';
      toast.error(msg);
      if (e.response?.status === 401) setCodeOk(false);
    } finally { setBusy(false); }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center text-muted-foreground px-4 text-center">{error}</div>;
  if (!info) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-sm" data-testid="scan-join-page">
        <CardContent className="pt-6 space-y-4 text-center">
          <ScanLine className="h-10 w-10 mx-auto text-emerald-400" />
          <div>
            <h1 className="text-xl font-bold">Check-in scanner access</h1>
            <p className="text-sm text-muted-foreground">{info.event?.name || info.event?.title}{info.event?.event_date ? ` · ${info.event.event_date}` : ''}</p>
          </div>
          {!codeOk ? (
            <>
              <Input inputMode="numeric" maxLength={4} className="text-center text-2xl tracking-[0.5em] font-mono h-14"
                placeholder="••••" value={passcode}
                onChange={e => setPasscode(e.target.value.replace(/\D/g, '').slice(0, 4))} data-testid="scan-join-passcode" />
              <Button onClick={join} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="scan-join-code-btn">Enter</Button>
              <p className="text-[11px] text-muted-foreground">Ask the organizer for the 4-digit passcode.</p>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">Quick intro so the organizer knows who's scanning:</p>
              <Input placeholder="Your name" value={name} onChange={e => setName(e.target.value)} data-testid="scan-join-name" />
              <Input type="email" placeholder="Email (optional)" value={email} onChange={e => setEmail(e.target.value)} data-testid="scan-join-email" />
              <Input type="tel" placeholder="Phone (optional)" value={phone} onChange={e => setPhone(e.target.value)} data-testid="scan-join-phone" />
              <Button onClick={join} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="scan-join-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Start scanning →
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
